import React, { useEffect } from "react";

// Styles
import styles from './swapCustomCutLayersModal.module.css';
import Spinner from "./spinner";
import { getBackendAPIDomain, getBackendApiUrl } from "../../../config";
import { addAuthorizationHeader } from "../../../api/network";

interface SwapCustomCutLayersProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (comment?: string) => void;
  wizardData: Record<string, any>;
  docId?: string;
}

const SwapCustomCutLayersModal: React.FC<SwapCustomCutLayersProps> = ({
  isOpen,
  onClose,
  onSuccess,
  wizardData,
  docId,
}) => {
  if (!isOpen) return null;

  //wizardData has the following properties:
  // - colorCount: the number of layers, it's a string that can be parsed as an integer and can be either 1, 2 or 3
  // - for each layer there is a material property that specifies the material used for that layer in the format "colorXMaterial" where X is the layer number (1, 2, or 3) and the property is a string
  // - for each layer there is a color property that specifies the color used for that layer in the format "colorXMaterialColor" where X is the layer number (1, 2, or 3) and the property is a string
  // - for each layer there is a description property that specifies the description for that layer in the format "colorXDescription" where X is the layer number (1, 2, or 3) and the property is a string
  // we want the component to display all the layers and their properties (material, color, description) and also the original position of that layer, so that they are piled up vertically
  // the description property must be editable (i.e. show it in a textbox and allow the user to change its value)
  // between each layer there should be a button to swap the layer above the button with the layer below
  // there should be a button in the bottom to confirm the swap operation
  // the button will dump to the console an object with the new colorXMaterial and colorXMaterialColor and colorXDescription for each layer after the swap operations
  //for the style we want to follow the same pattern as changeLayerNamesModal

  const [loading, setLoading] = React.useState<boolean>(true);
  const [processing, setProcessing] = React.useState<boolean>(false);
  const [invalidDescriptions, setInvalidDescriptions] = React.useState<boolean>(false);
  const colorCount = parseInt(wizardData.colorCount, 10);
  const layers = Array.from({ length: colorCount }, (_, i) => {
    const index = i + 1;
    return {
      originalPosition: index,
      material: wizardData[`color${index}Material`],
      color: wizardData[`color${index}MaterialColor`],
      description: wizardData[`color${index}Description`],
      displayColor: { colorName: "", colorHex: "" },
      displayMaterial: "",
    };
  });
  const [currentLayers, setCurrentLayers] = React.useState(layers);
  React.useEffect(() => {
    setInvalidDescriptions(currentLayers.some(layer => !layer.description));
  }, [currentLayers]);
  const swapLayers = (index: number) => {
    setCurrentLayers((prevLayers) => {
      const newLayers = [...prevLayers];
      const temp = newLayers[index];
      newLayers[index] = newLayers[index + 1];
      newLayers[index + 1] = temp;
      return newLayers;
    });
  };
  const confirmSwap = () => {
    setProcessing(true);
    const newLayerData = currentLayers.reduce((acc, layer, index) => {
      const layerIndex = index + 1;
      acc[`color${layerIndex}Material`] = layer.material;
      acc[`color${layerIndex}MaterialColor`] = layer.color;
      acc[`color${layerIndex}Description`] = layer.description;
      return acc;
    }, {} as Record<string, any>);

    const headers = addAuthorizationHeader({
      'Content-Type': 'application/json'
    });

    fetch(`${getBackendApiUrl()}/DesignLayerSwap`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        id: docId,
        layers: newLayerData,
      })
    }).then(async (processResp) => {
      if (!processResp.ok) throw new Error("Error swapping layers " + await processResp.text())
      setProcessing(false);
      onSuccess();
    }).catch(err => {
      console.log("Failed swap layers: " + (err as Error).message)
      setProcessing(false);
    });
  };
  
  //we want a button that is a svg with an arrow pointing up next to another arrow pointing down for swapping layers
  //the background color should be blue and the arrows should be white
  //the button should be the size of both arrows plus a small margin
  function SwapButton({ onClick }: { onClick: () => void }) {
    return (
      <button className={styles.swapButton} onClick={onClick} disabled={processing || invalidDescriptions}>
        <svg className="h-4 w-4 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
        <svg className="h-4 w-4 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V2" />
        </svg>
      </button>
    );
  }

  const colorSquare = (colorHex: string) => (
    <span className={styles.colorSquare} style={{ backgroundColor: colorHex }}></span>
  );

  const renderLayer = (layer: typeof layers[number], index: number) => (
    <div key={index} className={styles.layerWrapper}>
      <div key={index} className={styles.layerContainer}>
        <p>Original Position: {layer.originalPosition}</p>
        <p>Material: {layer.displayMaterial || layer.material}</p>
        <p>Color: {colorSquare(layer.displayColor?.colorHex || "#FFFFFF")} {layer.displayColor?.colorName || layer.color}</p>
        <p>Description: <input type="text" value={layer.description} disabled={processing} className={styles.descriptionInput} onChange={(e) => {
          const newDescription = e.target.value;
          setCurrentLayers(prevLayers => {
            const newLayers = [...prevLayers];
            newLayers[index] = { ...newLayers[index], description: newDescription };
            return newLayers;
          });
        }} /></p>
      </div>
      {index < currentLayers.length - 1 && (
        <SwapButton onClick={() => !processing && swapLayers(index)} />
      )}
    </div>
  );

  function CloseIcon() {
    return (
      <button type="button" className={`${styles.closeIcon} bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500`} disabled={processing}>
        <span className="sr-only">Close menu</span>
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    );
  }

  async function loadInitialData() {
    const response = await fetch(
      `${getBackendAPIDomain()}/stahls/13/artworkupload/mapjobdatadetails?mapName=cart`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(wizardData),
      }
    );
    const data = await response.json();

    const color1DisplayMaterial = data.color1Material && data.color1Material.val ? data.color1Material.val : "";
    const color2DisplayMaterial = data.color2Material && data.color2Material.val ? data.color2Material.val : "";
    const color3DisplayMaterial = data.color3Material && data.color3Material.val ? data.color3Material.val : "";

    //display colors have this format "<div class='color-swatch' style='background-color:#353842;'></div> <span class='color-name'>Navy</span>"
    // parse the display color HTML to extract colorName and colorHex
    const parseMaterialColor = (htmlString: string) => {
      const div = document.createElement("div");
      div.innerHTML = htmlString;
      const colorSwatch = div.querySelector(".color-swatch") as HTMLElement;
      const colorNameSpan = div.querySelector(".color-name") as HTMLElement;
      const colorHex = colorSwatch ? colorSwatch.style.backgroundColor : "";
      const colorName = colorNameSpan ? colorNameSpan.textContent || "" : "";
      return { colorName, colorHex };
    };
    const color1DisplayColor = data.color1MaterialColor && data.color1MaterialColor.val ? parseMaterialColor(data.color1MaterialColor.val) : { colorName: "", colorHex: "" };
    const color2DisplayColor = data.color2MaterialColor && data.color2MaterialColor.val ? parseMaterialColor(data.color2MaterialColor.val) : { colorName: "", colorHex: "" };
    const color3DisplayColor = data.color3MaterialColor && data.color3MaterialColor.val ? parseMaterialColor(data.color3MaterialColor.val) : { colorName: "", colorHex: "" };

    if(color1DisplayMaterial || color1DisplayColor) {
      setCurrentLayers(prevLayers => {
        const newLayers = [...prevLayers];
        newLayers[0] = { 
          ...newLayers[0], 
          displayMaterial: color1DisplayMaterial,
          displayColor: color1DisplayColor,
        };
        return newLayers;
      });
    }

    if (currentLayers.length > 1 && (color2DisplayMaterial || color2DisplayColor)) {
      setCurrentLayers(prevLayers => {
        const newLayers = [...prevLayers];
        newLayers[1] = { 
          ...newLayers[1], 
          displayMaterial: color2DisplayMaterial,
          displayColor: color2DisplayColor,
        };
        return newLayers;
      });
    }

    if (currentLayers.length > 2 && (color3DisplayMaterial || color3DisplayColor)) {
      setCurrentLayers(prevLayers => {
        const newLayers = [...prevLayers];
        newLayers[2] = { 
          ...newLayers[2], 
          displayMaterial: color3DisplayMaterial,
          displayColor: color3DisplayColor,
        };
        return newLayers;
      });
    }
  }

  useEffect(() => {
    loadInitialData().finally(() => {
      setLoading(false);
    });
  }, []);

  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modalContainer} relative w-auto my-auto mx-auto max-w-3xl`}>
        <div className={`${styles.modalWrapper} border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none`}>
          <div className={styles.header}>
            <span>Edit Layer Name</span>
            <div className={styles.closeIcon} onClick={processing ? undefined : onClose}>
              <CloseIcon />
            </div>
          </div>
          {loading ? (
              <Spinner spinning={true} />
            ) : (
              <>
                <div className={styles.modalContent}>
                  {currentLayers.map(renderLayer)}
                </div>
                <div className={`${styles.footerButtons} p-6 border-t border-solid border-blueGray-200 rounded-b`}>
                  <button className={styles.acceptBtn} onClick={confirmSwap} disabled={processing}>Confirm Swap</button>
                  <button className={styles.cancelBtn} onClick={onClose} disabled={processing}>Cancel</button>
                </div>
              </>
            )
          }
        </div>
      </div>
    </div>
  );
};

export default SwapCustomCutLayersModal;